import React from "react";
import { 
  ChefHat, 
  Bed, 
  Bath, 
  Tv, 
  Wifi, 
  TreePine, 
  Utensils,
  Shirt,
  CookingPot,
  Target,
  Baby,
  Heart,
  ShirtIcon,
  Sparkles
} from "lucide-react";

const AmenitiesSection = () => {
  const amenities = [
    {
      icon: ChefHat,
      title: "Cuisine équipée",
      description: "Tout le nécessaire pour préparer vos repas"
    },
    {
      icon: Bed,
      title: "3 chambres + salon convertible",
      description: "Literie confortable pour 10 personnes"
    },
    {
      icon: Bath,
      title: "3 salles d'eau modernes",
      description: "Salles de bain spacieuses et fonctionnelles"
    },
    {
      icon: Wifi,
      title: "Wi-Fi gratuit",
      description: "Connexion internet haut débit"
    },
    {
      icon: Tv,
      title: "Télévision",
      description: "Moments de détente en famille"
    },
    {
      icon: TreePine,
      title: "Terrasse & jardin",
      description: "Espaces extérieurs pour profiter de la nature"
    },
    {
      icon: CookingPot,
      title: "Barbecue & terrain de pétanque",
      description: "Grillades et jeux en plein air"
    },
    {
      icon: Utensils,
      title: "Lave-vaisselle",
      description: "Confort moderne pour vos séjours"
    },
    {
      icon: Shirt,
      title: "Lave-linge",
      description: "Parfait pour les séjours prolongés"
    },
    {
      icon: Baby,
      title: "Lit bébé et chaise haute",
      description: "Équipements sur demande"
    },
    {
      icon: Sparkles,
      title: "Draps inclus & lits faits",
      description: "Lits préparés à votre arrivée"
    }
  ];

  return (
    <section 
      id="amenities" 
      className="section-padding bg-[#EDE6DB]/20"
      aria-labelledby="amenities-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête responsive */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            id="amenities-title"
            className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Équipements & Confort
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Tout est pensé pour votre bien-être dans notre gîte 3 clés Clévacances
          </p>
        </div>

        {/* Grille responsive des équipements */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {amenities.map((amenity, index) => {
            const IconComponent = amenity.icon;
            return (
              <div
                key={index}
                className="group card-hover bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-[#EDE6DB]/50 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#1C3A27]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-[#1C3A27] transition-colors duration-300">
                    <IconComponent 
                      size={24} 
                      className="sm:w-8 sm:h-8 text-[#1C3A27] group-hover:text-white transition-colors duration-300" 
                    />
                  </div>
                  
                  <h3 className="font-semibold text-base sm:text-lg text-[#1C3A27] mb-2">
                    {amenity.title}
                  </h3>
                  
                  <p className="text-[#1C3A27]/70 text-sm leading-relaxed">
                    {amenity.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Informations supplémentaires - Services */}
        <div className="mt-12 md:mt-16">
          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EDE6DB]/50 max-w-5xl mx-auto">
            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#1C3A27] mb-6 text-center">
              Services inclus & options
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-[#1C3A27]/80">
              <div className="flex items-start space-x-3">
                <Sparkles size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Draps inclus</div>
                  <div className="text-sm">Lits faits à l'arrivée</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Shirt size={20} className="text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Serviettes non incluses</div>
                  <div className="text-sm">Option 6 €/personne pour le séjour</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Sparkles size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Ménage obligatoire</div>
                  <div className="text-sm">70 € par séjour</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Target size={20} className="text-purple-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Stationnement gratuit</div>
                  <div className="text-sm">À proximité immédiate</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Heart size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Animaux acceptés</div>
                  <div className="text-sm">Sans supplément</div>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Sparkles size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-1">Accès autonome</div>
                  <div className="text-sm">Boîte à clé sécurisée</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AmenitiesSection;